import { useEffect, useCallback, useRef } from 'react';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { AUTH_TOKEN } from '../api/constants';
import { ROUTES } from '../lib/routes';
import { useDispatch } from 'react-redux';

const BAD_TOKEN_LOGOUT_DELAY = 4 * 20 * 60 * 1000;
const TOKEN_CHECK_INTERVAL = 30 * 1000;

const isTokenExpired = (token: string): boolean => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        return payload.exp < currentTime;
    } catch (error) {
        return true;
    }
};

const isTokenValid = (token: string): boolean => {
    if (!token) return false;
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    return !isTokenExpired(token);
};

export const useSessionTimeout = () => {
    const router = useRouter();
    const dispatch = useDispatch();

    const badTokenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const tokenCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const logoutUser = useCallback(() => {
        dispatch.auth.setSessionExpired(true);
        dispatch.auth.setLoggedIn(false);
        Cookies.remove(AUTH_TOKEN);
        Cookies.remove('username');
        router.replace(ROUTES.LOGIN);

        if (badTokenTimerRef.current) {
            clearTimeout(badTokenTimerRef.current);
            badTokenTimerRef.current = null;
        }
        if (tokenCheckIntervalRef.current) {
            clearInterval(tokenCheckIntervalRef.current);
            tokenCheckIntervalRef.current = null;
        }
    }, [dispatch.auth, router]);

    const startBadTokenCountdown = useCallback(() => {
        console.log('Bad token detected - starting 20 minute countdown');

        if (badTokenTimerRef.current) {
            clearTimeout(badTokenTimerRef.current);
        }

        badTokenTimerRef.current = setTimeout(() => {
            console.log('20 minutes elapsed since bad token - logging out');
            logoutUser();
        }, BAD_TOKEN_LOGOUT_DELAY);
    }, [logoutUser]);

    const checkTokenStatus = useCallback(() => {
        const token = Cookies.get(AUTH_TOKEN);

        if (!token || !isTokenValid(token)) {
            console.log('Token is invalid or missing');

            if (!badTokenTimerRef.current) {
                startBadTokenCountdown();
            }
            return false;
        } else {
            if (badTokenTimerRef.current) {
                console.log('Token is now valid - clearing bad token timer');
                clearTimeout(badTokenTimerRef.current);
                badTokenTimerRef.current = null;
            }
            return true;
        }
    }, [startBadTokenCountdown]);

    useEffect(() => {
        checkTokenStatus();

        tokenCheckIntervalRef.current = setInterval(checkTokenStatus, TOKEN_CHECK_INTERVAL);

        return () => {
            if (tokenCheckIntervalRef.current) {
                clearInterval(tokenCheckIntervalRef.current);
            }
            if (badTokenTimerRef.current) {
                clearTimeout(badTokenTimerRef.current);
            }
        };
    }, [checkTokenStatus]);
};
